import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import BaseClass from 'components/BaseClass';
import { wrapComponent, wrapFunctionalComponent } from 'utils/framework';
import useCartIntentReplay from 'hooks/useCartIntentReplay';
import appliedPromotionsSelector from 'selectors/basket/appliedPromotions/appliedPromotionsSelector';

import { DebouncedResize } from 'constants/events';

import {
    Container, Flex, Grid, Link, Text
} from 'components/ui';
import CostSummary from 'components/RwdBasket/RwdBasketLayout/CostSummary/CostSummary';
import GiftCardQuickAdd from 'components/RwdBasket/RwdBasketLayout/GiftCardQuickAdd/GiftCardQuickAdd';
import BIBenefits from 'components/RwdBasket/RwdBasketLayout/BIBenefits';
import CustomerLimitBox from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentCustomerLimitMessage/CustomerLimitBox';
import isFunction from 'utils/functions/isFunction';
import mediaUtils from 'utils/Media';
import localeUtils from 'utils/LanguageLocale';
import agentAwareUtils from 'utils/AgentAware';
import RwdBasketUtils from 'utils/RwdBasket';
import { breakpoints, colors } from 'style/config';
import DiscountLimitBox from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentCustomerLimitMessage/DiscountLimitBox';
import { AgentBasketSearch } from 'components/AgentAware';
import RwdBasketLayoutEN from 'components/RwdBasket/RwdBasketLayout/locales/RwdBasketLayout_en_US';
import RwdBasketLayoutFR from 'components/RwdBasket/RwdBasketLayout/locales/RwdBasketLayout_fr_CA';
import TopOfBasketErrors from 'components/RwdBasket/RwdBasketLayout/TopOfBasketError/TopOfBasketError.f';

const { Media } = mediaUtils;

const INSIDE_MAIN_COLUMN = 'insideMainColumn';
const OUTSIDE_MAIN_COLUMN = 'outsideMainColumn';
const getText = localeUtils.getLocalisedDataFromResource(RwdBasketLayoutEN, RwdBasketLayoutFR);

// SIM-28 (ACS-4473): thin functional bridge -- RwdBasketLayout is a class component and cannot
// consume hooks directly. Renders nothing; only replays a persisted cart intent on basket mount.
const CartIntentReplayBridge = ({ basketLoaded }) => {
    const appliedPromotions = useSelector(appliedPromotionsSelector, shallowEqual);
    useCartIntentReplay(basketLoaded, appliedPromotions);

    return null;
};
const WrappedCartIntentReplayBridge = wrapFunctionalComponent(CartIntentReplayBridge, 'CartIntentReplayBridge');

function BasketSwitch({ basketSwitchLabel, onBasketSwitch, mediaQuery }) {
    return (
        <Media {...mediaQuery}>
            <Link
                color='blue'
                onClick={onBasketSwitch}
                children={basketSwitchLabel}
            />
        </Media>
    );
}

function Header({ basketSwitchLabel, goToPreBasket, onBasketSwitch }) {
    return (
        <Flex
            alignItems='baseline'
            justifyContent='space-between'
            marginBottom={4}
            flexWrap='nowrap'
        >
            <Link
                arrowPosition='before'
                arrowDirection='left'
                onClick={goToPreBasket}
                children={getText('backButton')}
            />
            <BasketSwitch
                basketSwitchLabel={basketSwitchLabel}
                onBasketSwitch={onBasketSwitch}
                mediaQuery={{ at: 'xs' }}
            />
        </Flex>
    );
}

function SavedForLaterOutsideMainColumn({ showSaveForLater, items, cmsComponentListPosition }) {
    if (!showSaveForLater || !items?.length || cmsComponentListPosition !== OUTSIDE_MAIN_COLUMN) {
        return null;
    }

    return <>{items}</>;
}

function MainColumn({
    cartSections,
    topContentMessageComponentList,
    messageInfo: { topOfBasketErrors },
    title,
    basketSwitchLabel,
    onBasketSwitch,
    isNavigationAvailable,
    bottomContentComponentList,
    cmsComponentListPosition,
    refreshBasketClick,
    showBasketGreyBackground,
    sedClosePromoMessages,
    promoErrorMessages,
    showSaveForLater,
    savedForLaterCarouselItems,
    oosBiOrSample
}) {
    let styledCartSections = cartSections;
    let styledTopContentMessages = topContentMessageComponentList.messages;
    let styledBottomContentMessages = bottomContentComponentList.items;

    if (showBasketGreyBackground) {
        styledCartSections = RwdBasketUtils.applyTopLevelBackground(cartSections, showBasketGreyBackground);

        if (topContentMessageComponentList.isAvailable) {
            styledTopContentMessages = RwdBasketUtils.applyTopLevelBackground(topContentMessageComponentList.messages, showBasketGreyBackground);
        }

        if (bottomContentComponentList.isAvailable) {
            styledBottomContentMessages = RwdBasketUtils.applyShowBasketGreyBackgroundToProductLists(
                bottomContentComponentList.items,
                showBasketGreyBackground
            );
        }
    }

    return (
        <Flex
            flexDirection='column'
            gap={[4, Sephora.isAgent ? 4 : 5]}
        >
            <Flex
                alignItems='baseline'
                justifyContent='space-between'
                flexWrap='nowrap'
            >
                <Text
                    is='h2'
                    fontSize={['lg', 'xl']}
                    fontWeight='bold'
                    data-at={Sephora.debug.dataAt('basket_header')}
                    children={title}
                />
                {isNavigationAvailable && (
                    <BasketSwitch
                        basketSwitchLabel={basketSwitchLabel}
                        onBasketSwitch={onBasketSwitch}
                        mediaQuery={{ greaterThan: 'xs' }}
                    />
                )}
            </Flex>

            {Sephora.isAgent && (
                <div
                    style={{ display: 'none' }}
                    className={agentAwareUtils.applyShowAgentAwareClass()}
                >
                    <div
                        id='agent-aware-basket-search'
                        style={{ width: '100%' }}
                    ></div>
                    <button
                        id='agent-aware-refresh-basket'
                        style={{ display: 'none' }}
                        onClick={refreshBasketClick}
                    />
                </div>
            )}
            {Sephora.isAgent && agentAwareUtils.isAgentAwareSession() && <AgentBasketSearch refreshBasketClick={refreshBasketClick} />}

            {(topOfBasketErrors.isAvailable || topContentMessageComponentList.isAvailable) && (
                <Flex
                    gap={2}
                    flexDirection={'column'}
                    alignItems={'flex-start'}
                >
                    {sedClosePromoMessages && <CustomerLimitBox error={sedClosePromoMessages} />}

                    {promoErrorMessages && promoErrorMessages?.errorMessages?.length > 0 && <DiscountLimitBox error={promoErrorMessages} />}

                    <TopOfBasketErrors
                        topOfBasketErrors={topOfBasketErrors}
                        oosBiOrSample={oosBiOrSample}
                    />
                    {topContentMessageComponentList.isAvailable && <>{styledTopContentMessages}</>}
                </Flex>
            )}
            {styledCartSections}
            <SavedForLaterInsideMainColumn
                showSaveForLater={showSaveForLater}
                items={savedForLaterCarouselItems}
                cmsComponentListPosition={cmsComponentListPosition}
            />
            {bottomContentComponentList.isAvailable && cmsComponentListPosition === INSIDE_MAIN_COLUMN && styledBottomContentMessages}
        </Flex>
    );
}

function SavedForLaterInsideMainColumn({ showSaveForLater, items, cmsComponentListPosition }) {
    if (!showSaveForLater || !items?.length || cmsComponentListPosition !== INSIDE_MAIN_COLUMN) {
        return null;
    }

    return <>{items}</>;
}

function SecondaryColumn({
    paymentInfo,
    addMarginTop,
    biBenefitsInfo,
    giftCardInfo,
    onBasketSwitch,
    swapGiftCardBiBenefitBasket,
    showBasketGreyBackground,
    basketType, //TS-3142 - showApplyPointsForBazaarItems
    toggleCostSummary,
    costSummaryCollapsed,
    isSmallView
}) {
    const isEmptyBasket = paymentInfo.totalItems === 0;
    const summaryDropdownDisplay = localeUtils.isCanada() && !isSmallView && !isEmptyBasket;
    const summaryDoubleDisplay = localeUtils.isUS() && !isSmallView && !isEmptyBasket;

    return (
        <Flex
            flexDirection='column'
            gap={[4, 5]}
            marginTop={[null, null, null, addMarginTop && 2]}
        >
            <CostSummary
                paymentInfo={paymentInfo}
                showBasketGreyBackground={showBasketGreyBackground}
                summaryDropdownDisplay={summaryDropdownDisplay}
                summaryDoubleDisplay={summaryDoubleDisplay}
                {...(summaryDoubleDisplay && {
                    summaryDoubleDisplay: true,
                    hideSummary: true
                })}
                {...(summaryDropdownDisplay && {
                    toggleCostSummary,
                    costSummaryCollapsed,
                    summaryDropdownDisplay
                })}
                {...(showBasketGreyBackground && { backgroundColor: colors.white })}
            />
            {giftCardInfo.isAvailable && (
                <GiftCardQuickAdd
                    order={swapGiftCardBiBenefitBasket ? [2] : [1, 2]}
                    showBasketGreyBackground={showBasketGreyBackground}
                    {...(showBasketGreyBackground && { backgroundColor: colors.white })}
                />
            )}
            <BIBenefits
                biBenefitsInfo={biBenefitsInfo}
                onBasketSwitch={onBasketSwitch}
                {...(swapGiftCardBiBenefitBasket && { order: [1] })}
                showBasketGreyBackground={showBasketGreyBackground}
                {...(showBasketGreyBackground && { backgroundColor: colors.white, ...altStyles.boxShadow })}
                basketType={basketType} //TS-3142 - showApplyPointsForBazaarItems
            />
            {summaryDoubleDisplay && (
                <CostSummary
                    paymentInfo={paymentInfo}
                    showBasketGreyBackground={showBasketGreyBackground}
                    {...(summaryDoubleDisplay && {
                        hideCheckoutButton: true,
                        summaryDoubleDisplay: true
                    })}
                    {...(showBasketGreyBackground && { backgroundColor: colors.white })}
                />
            )}
        </Flex>
    );
}

class RwdBasketLayout extends BaseClass {
    constructor(props) {
        super(props);

        this.state = {
            cmsComponentListPosition: null,
            costSummaryCollapsed: true,
            isSmallView: null
        };
    }

    calculateCMSComponentListPosition = () => {
        const isLG = window.matchMedia(breakpoints.lgMin).matches;

        this.setState({
            cmsComponentListPosition: isLG ? OUTSIDE_MAIN_COLUMN : INSIDE_MAIN_COLUMN
        });
    };

    componentDidMount() {
        this.calculateCMSComponentListPosition();
        this.handleResize();

        window.addEventListener(DebouncedResize, this.calculateCMSComponentListPosition);
        window.addEventListener(DebouncedResize, this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener(DebouncedResize, this.handleResize);
        window.removeEventListener(DebouncedResize, this.calculateCMSComponentListPosition);
    }

    toggleCostSummary = () => {
        this.setState({ costSummaryCollapsed: false });
    };

    handleResize = () => {
        const showSmallView = window.matchMedia(breakpoints.smMax).matches;

        if (this.state.isSmallView !== showSmallView) {
            this.setState({ isSmallView: showSmallView });
        }
    };

    render = () => {
        const {
            title,
            basketSwitchLabel,
            onBasketSwitch,
            goToPreBasket,
            cartSections,
            paymentInfo,
            messageInfo,
            biBenefitsInfo,
            giftCardInfo,
            topContentMessageComponentList,
            bottomContentComponentList,
            refreshBasketClick,
            swapGiftCardBiBenefitBasket,
            showBasketGreyBackground,
            sedClosePromoMessages,
            promoErrorMessages,
            basketType, //TS-3142 - showApplyPointsForBazaarItems
            showMoveBasketCheckoutButton,
            showSaveForLater,
            savedForLaterCarouselItems,
            oosBiOrSample
        } = this.props;

        const isNavigationAvailable = isFunction(onBasketSwitch) && isFunction(goToPreBasket);
        window.dispatchEvent(new CustomEvent('AgentAwareContainerRendered', { detail: { id: 'agent-aware-basket-search' } }));

        return (
            <>
                <WrappedCartIntentReplayBridge basketLoaded={Boolean(cartSections)} />
                <Container
                    paddingX={0}
                    paddingTop={4}
                    {...(showBasketGreyBackground && { css: altStylesGreyBackground })}
                >
                    {isNavigationAvailable && (
                        <Header
                            basketSwitchLabel={basketSwitchLabel}
                            goToPreBasket={goToPreBasket}
                            onBasketSwitch={onBasketSwitch}
                        />
                    )}
                    <Grid
                        columns={[null, null, null, 'auto 400px']}
                        gap={[4, 5]}
                    >
                        <MainColumn
                            cartSections={cartSections}
                            title={title}
                            basketSwitchLabel={basketSwitchLabel}
                            onBasketSwitch={onBasketSwitch}
                            messageInfo={messageInfo}
                            topContentMessageComponentList={topContentMessageComponentList}
                            isNavigationAvailable={isNavigationAvailable}
                            bottomContentComponentList={bottomContentComponentList}
                            cmsComponentListPosition={this.state.cmsComponentListPosition}
                            refreshBasketClick={refreshBasketClick}
                            showBasketGreyBackground={showBasketGreyBackground}
                            sedClosePromoMessages={sedClosePromoMessages}
                            promoErrorMessages={promoErrorMessages}
                            showSaveForLater={showSaveForLater}
                            savedForLaterCarouselItems={savedForLaterCarouselItems}
                            oosBiOrSample={oosBiOrSample}
                        />
                        <SecondaryColumn
                            paymentInfo={paymentInfo}
                            addMarginTop={!isNavigationAvailable}
                            biBenefitsInfo={biBenefitsInfo}
                            giftCardInfo={giftCardInfo}
                            onBasketSwitch={onBasketSwitch}
                            swapGiftCardBiBenefitBasket={swapGiftCardBiBenefitBasket}
                            showBasketGreyBackground={showBasketGreyBackground}
                            basketType={basketType} //TS-3142 - showApplyPointsForBazaarItems
                            showMoveBasketCheckoutButton={showMoveBasketCheckoutButton}
                            isSmallView={this.state.isSmallView}
                            toggleCostSummary={this.toggleCostSummary}
                            costSummaryCollapsed={this.state.costSummaryCollapsed}
                        />
                    </Grid>
                    <SavedForLaterOutsideMainColumn
                        showSaveForLater={showSaveForLater}
                        items={savedForLaterCarouselItems}
                        cmsComponentListPosition={this.state.cmsComponentListPosition}
                    />
                    {bottomContentComponentList.isAvailable &&
                        this.state.cmsComponentListPosition === OUTSIDE_MAIN_COLUMN &&
                        bottomContentComponentList.items}
                </Container>
            </>
        );
    };
}

const altStyles = {
    boxShadow: {
        boxShadow: 'light'
    }
};

const altStylesGreyBackground = {
    '&::before': {
        content: '\'\'',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: colors.nearWhite,
        zIndex: -1
    }
};

export default wrapComponent(RwdBasketLayout, 'RwdBasketLayout', true);
